import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useBrandingStore } from '../store/branding'
import { api } from '../lib/api'
import { GlowCard } from '../components/ui/GlowCard'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { Checkbox } from '../components/ui/Checkbox'

function MailIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  )
}

function LockIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPass, setShowPass] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingSetup, setCheckingSetup] = useState(true)
  const { setAuth, isAuthenticated } = useAuthStore()
  const { companyName } = useBrandingStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
      return
    }
    api.get<{ needsSetup: boolean }>('/auth/check-setup')
      .then(({ needsSetup }) => {
        if (needsSetup) navigate('/setup')
      })
      .catch(() => {})
      .finally(() => setCheckingSetup(false))
  }, [isAuthenticated, navigate])

  async function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await api.post<{ token: string; user: { slug: string; email: string; full_name: string; role: string }; needsSetup?: boolean }>('/auth/login', { email, password })

      if ('needsSetup' in res) {
        navigate('/setup')
        return
      }

      setAuth(res.token, res.user)
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && password) handleSubmit()
  }

  if (checkingSetup) return null

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-0)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Ambient glow */}
      <div style={{
        position: 'absolute',
        top: '-20%',
        left: '50%',
        width: '80vw',
        height: '60vh',
        background: 'radial-gradient(ellipse at center, var(--accent-g), transparent 70%)',
        transform: 'translateX(-50%)',
        animation: 'breathe 8s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      {/* Login card area */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: 400,
        padding: '0 24px',
      }}>
        {/* Logo + heading */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: 36,
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 'var(--r-l)',
            background: 'linear-gradient(135deg, var(--accent), var(--accent-h))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--sh-glow-s)',
            marginBottom: 16,
          }}>
            <span style={{
              fontSize: 22,
              fontWeight: 700,
              color: 'var(--accent-t)',
              fontFamily: 'var(--font)',
            }}>A</span>
          </div>
          <h1 style={{
            fontSize: 22,
            fontWeight: 600,
            color: 'var(--text-1)',
            fontFamily: 'var(--font)',
            marginBottom: 4,
          }}>Welcome back</h1>
          <p style={{
            fontSize: 13,
            color: 'var(--text-3)',
            fontFamily: 'var(--font)',
          }}>Sign in to {companyName || 'Amber Portal'}</p>
        </div>

        {/* Form card */}
        <GlowCard style={{ padding: 24 }}>
          {error && (
            <div style={{
              background: 'var(--err-m)',
              border: '1px solid rgba(248,113,113,.2)',
              borderRadius: 'var(--r-m)',
              padding: '10px 14px',
              marginBottom: 16,
              fontSize: 13,
              color: 'var(--err)',
              fontFamily: 'var(--font)',
            }}>{error}</div>
          )}

          <Input
            label="Email"
            placeholder="you@company.com"
            icon={MailIcon}
            value={email}
            onChange={setEmail}
            type="email"
            onKeyDown={handleKeyDown}
          />

          <Input
            label="Password"
            placeholder="••••••••"
            icon={LockIcon}
            value={password}
            onChange={setPassword}
            type={showPass ? 'text' : 'password'}
            onKeyDown={handleKeyDown}
            suffix={
              <button type="button" onClick={() => setShowPass(!showPass)} tabIndex={-1}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', display: 'flex', padding: 0 }}>
                {showPass ? (
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            }
          />

          {/* Remember me + Forgot password */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            marginTop: -4,
          }}>
            <Checkbox label="Remember me" size="sm" />
            <a href="/forgot-password" style={{
              fontSize: 12,
              color: 'var(--accent)',
              textDecoration: 'none',
              fontFamily: 'var(--font)',
            }}>Forgot password?</a>
          </div>

          {/* Sign in button */}
          <Button
            size="lg"
            style={{ width: '100%' }}
            onClick={() => handleSubmit()}
            disabled={!email || !password}
            loading={loading}
          >
            Sign in
          </Button>
        </GlowCard>

        {/* Footer */}
        <p style={{
          textAlign: 'center',
          marginTop: 24,
          fontSize: 12,
          color: 'var(--text-3)',
          fontFamily: 'var(--font)',
        }}>
          Don&apos;t have an account?{' '}
          <a href="/register" style={{
            color: 'var(--accent)',
            textDecoration: 'none',
          }}>Start free trial</a>
        </p>
      </div>

      <style>{`
        @keyframes breathe {
          0%, 100% { opacity: 0.3; transform: translateX(-50%) scale(1); }
          50% { opacity: 0.5; transform: translateX(-50%) scale(1.05); }
        }
      `}</style>
    </div>
  )
}
